/* begin general import */
import { Col, Row, Spin } from "antd";
import { AppUserGrouping } from "models/AppUserGrouping";
import { Status, StatusFilter } from "models/Status";
import React from "react";
import { useTranslation } from "react-i18next";
import { Model } from "react3l-common";
import { Modal, Select } from "react3l-ui-library";

import { DrawerProps } from "react3l-ui-library/build/components/Drawer/Drawer";
import FormItem from "react3l-ui-library/build/components/FormItem";
import InputText from "react3l-ui-library/build/components/Input/InputText";
import { projectDocumentUserGroupingRepository } from "repositories/project-document-user-grouping-repository";
import nameof from "ts-nameof.macro";
import { utilService } from "services/common-services/util-service";
import { filterService } from "services/page-services/filter-service";
import { Organization, OrganizationFilter } from "models/Organization";
import { authorizationService } from "services/common-services/authorization-service";
import { APP_USER_GROUPING_PROJECT_PROFILE_ROUTE } from "config/route-consts";
import { API_PROJECT_DOCUMENT_USER_GROUPING_PREFIX } from "config/api-consts";
import { Close20 } from "@carbon/icons-react";
import TreeSelect from "components/TreeSelect";
import { of } from "rxjs";

interface AppUserGroupingProjectProfileDetailDrawerProps extends DrawerProps {
  model: AppUserGrouping;
  handleChangeSingleField?: (config: {
    fieldName: string;
  }) => (value: any) => void;
  handleChangeTreeField?: (config: {
    fieldName: string;
  }) => (values: any[], isMultiple: boolean) => void;
  handleChangeAllField?: (data: any) => void;
  loading?: boolean;
  currentNode?: AppUserGrouping;
  handleChangeSelectField: (config: {
    fieldName: string;
  }) => (idValue: number, value: Model) => void;
  currentOrganization?: Organization;
  // Trạng thái loading khi đang lưu — làm mờ + hiện spinner giữa form (không dùng để
  // disable/đổi nội dung nút "Lưu", giữ nút Lưu bình thường).
  loadingType?: "default" | "submitting" | "submitted" | "error";
  readOnly?: boolean;
  modalTitle?: React.ReactNode;
}

const CODE_MAX_LENGTH = 50;
const NAME_MAX_LENGTH = 256;
const getEmptyStatusList = () => of<Status[]>([]);

function AppUserGroupingProjectProfileDetailDrawer(
  props: AppUserGroupingProjectProfileDetailDrawerProps
) {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    APP_USER_GROUPING_PROJECT_PROFILE_ROUTE.split("/")[
      APP_USER_GROUPING_PROJECT_PROFILE_ROUTE.split("/").length - 1
    ],
    API_PROJECT_DOCUMENT_USER_GROUPING_PREFIX
  );

  const {
    model,
    handleChangeTreeField,
    handleChangeSingleField,
    handleChangeAllField,
    handleChangeSelectField,
    loading,
    visible,
    handleSave,
    handleCancel,
    currentOrganization,
    loadingType,
    readOnly = false,
    modalTitle,
  } = props;

  // Sửa nhóm đã tồn tại: khóa Mã nhóm. Đơn vị quản lý chỉ khóa khi là node con
  // (kế thừa đơn vị từ nhóm cha), cho phép chọn lại khi sửa nhóm gốc.
  const codeDisabled = readOnly || (model?.id !== undefined && model?.id !== null);
  const organizationDisabled = readOnly || !!model?.parentId;

  const [statusList] = filterService.useEnumList<Status>(
    readOnly
      ? getEmptyStatusList
      : projectDocumentUserGroupingRepository.singleListStatus
  );

  React.useEffect(() => {
    if (readOnly) {
      return;
    }
    if (model?.statusId === undefined && statusList?.length > 0) {
      const newModel: AppUserGrouping = { ...model };
      newModel.statusId = statusList[1]?.id;
      newModel.status = statusList[1];
      if (currentOrganization?.id) {
        newModel.organization = currentOrganization;
        newModel.organizationId = currentOrganization?.id;
      }
      handleChangeAllField({ ...newModel });
    }
  }, [
    currentOrganization,
    handleChangeAllField,
    handleChangeSelectField,
    model,
    readOnly,
    statusList,
  ]);

  const handleChangeStatus = React.useCallback(
    (statusId: number, status: Status) => {
      if (statusId !== null) {
        handleChangeSelectField({ fieldName: "status" })(statusId, status);
      } else {
        handleChangeSelectField({ fieldName: "status" })(1, undefined);
      }
    },

    [handleChangeSelectField]
  );
  return (
    <Modal
      size={720}
      visibleFooter={true}
      visible={visible}
      title={
        modalTitle ??
        (model?.id
          ? translate("general.detail.title")
          : translate("general.actions.create"))
      }
      handleSave={
        !readOnly &&
        ((!model?.id && validAction("create")) ||
        (model?.id && validAction("update")))
          ? handleSave
          : undefined
      }
      handleCancel={handleCancel}
      titleButtonCancel={translate("general.actions.close")}
      titleButtonApply={translate("general.actions.save")}
      closable={false}
      wrapClassName={
        !readOnly &&
        ((!model?.id && validAction("create")) ||
        (model?.id && validAction("update")))
          ? "dxg-modal-wrap"
          : "dxg-modal-wrap-one-btn"
      }
    >
      <div className="modal-content">
        {loading ? (
          <div
            className="loading-block d-flex align-items-center justify-content-center"
            style={{ minHeight: 300 }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <Spin spinning={loadingType === "submitting"}>
            <Row className="d-flex">
              <Col lg={24}>
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col lg={12} className="m-b--sm m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.code)
                      )}
                      message={model.errors?.code}
                    >
                      <InputText
                        label={translate("appUserGroupings.code")}
                        type={0}
                        isRequired
                        value={model.code}
                        placeHolder={translate(
                          "appUserGroupings.placeholder.code"
                        )}
                        className={"tio-account_square_outlined"}
                        onChange={handleChangeSingleField({
                          fieldName: nameof(model.code),
                        })}
                        disabled={codeDisabled}
                        maxLength={CODE_MAX_LENGTH}
                      />
                    </FormItem>
                  </Col>

                  <Col lg={12} className="m-b--sm m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.name)
                      )}
                      message={model.errors?.name}
                    >
                      <InputText
                        isRequired
                        label={translate("appUserGroupings.name")}
                        type={0}
                        value={model.name}
                        placeHolder={translate(
                          "appUserGroupings.placeholder.name"
                        )}
                        className={"tio-account_square_outlined"}
                        onChange={handleChangeSingleField({
                          fieldName: nameof(model.name),
                        })}
                        disabled={readOnly}
                        maxLength={NAME_MAX_LENGTH}
                      />
                    </FormItem>
                  </Col>

                  <Col lg={12} className="m-b--sm m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.status)
                      )}
                      message={model.errors?.status}
                    >
                      <Select
                        label={translate("appUserGroupings.status")}
                        type={0}
                        classFilter={StatusFilter}
                        searchProperty={"name"}
                        placeHolder={translate(
                          "appUserGroupings.placeholder.status"
                        )}
                        getList={projectDocumentUserGroupingRepository.singleListStatus}
                        onChange={handleChangeStatus}
                        value={model.status}
                        disabled={readOnly}
                      />
                    </FormItem>
                  </Col>

                  <Col lg={12} className="m-b--sm m-t--sm">
                    <FormItem
                      validateStatus={utilService.getValidateStatus(
                        model,
                        nameof(model.organization)
                      )}
                      message={model.errors?.organization}
                    >
                      <TreeSelect
                        isRequired
                        label={translate("appUserGroupings.organization")}
                        type={0}
                        placeHolder={translate(
                          "appUserGroupings.placeholder.organization"
                        )}
                        selectable={true}
                        classFilter={OrganizationFilter}
                        onChange={(values: any[]) => {
                          const internalValues =
                            values && values?.length > 0
                              ? values.map((value: Organization) => {
                                  value.organizationId = value?.id;
                                  value.appUserGroupingId = 0;
                                  return value;
                                })
                              : [];
                          handleChangeTreeField({
                            fieldName: nameof(
                              model.appUserGroupingOrganizationMappings
                            ),
                          })(internalValues, true);
                        }}
                        checkStrictly
                        getTreeData={
                          projectDocumentUserGroupingRepository.singleListOrganization
                        }
                        checkable
                        isUsingSearch
                        searchProperty="search"
                        searchType=""
                        disabled={organizationDisabled}
                        listItem={model.appUserGroupingOrganizationMappings}
                        render={(object: Organization) => {
                          return object?.code && object?.name
                            ? `${object?.code} - ${object?.name}`
                            : null;
                        }}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Spin>
        )}
      </div>

      <span className={"close-modal"} onClick={handleCancel}>
        <Close20 />
      </span>
    </Modal>
  );
}

export default AppUserGroupingProjectProfileDetailDrawer;
